/* eslint-disable react-hooks/set-state-in-effect -- pattern is controlled-prop sync */
/* Balance Sheet card — inline editable financial statement.
 * Mirrors the UX of DebtScheduleCard / UseOfFundsScheduleCard.
 *
 * Used on:
 *   - Authenticated review screen (broker / underwriter / admin)
 *   - Public balance-sheet share page (read+write)
 *   - Public shared underwriting link (read-only)
 *
 * Props:
 *   applicationId   (auth)         — app id
 *   linkToken       (public form)  — when set, public endpoints are used
 *   sharedUnderwritingToken (read) — when set, hides edit + uses shared-uw PDF
 *   data            existing balance_sheet_data dict
 *   onSave          callback after save
 *   authToken       JWT (auth)
 *   businessName    optional
 *   readOnly        boolean
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  ClipboardList, Pencil, Download, Loader2, Link2, Save,
  CheckCircle, AlertCircle, Upload, FileText as FileTextIcon,
  ChevronDown, ChevronRight, Trash2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const BALANCE_SHEET_GROUPS = [
  {
    key: 'assets',
    label: 'Assets',
    accent: 'emerald',
    subsections: [
      { key: 'current', label: 'Current Assets', fields: [
        { k: 'cash', l: 'Cash & Cash Equivalents' },
        { k: 'accounts_receivable', l: 'Accounts Receivable' },
        { k: 'inventory', l: 'Inventory' },
        { k: 'prepaid_expenses', l: 'Prepaid Expenses' },
        { k: 'other_current_assets', l: 'Other Current Assets' },
      ]},
      { key: 'non_current', label: 'Non-Current Assets', fields: [
        { k: 'property_equipment', l: 'Property & Equipment (net)' },
        { k: 'real_estate', l: 'Real Estate' },
        { k: 'vehicles', l: 'Vehicles' },
        { k: 'intangible_assets', l: 'Intangible Assets' },
        { k: 'investments', l: 'Investments' },
        { k: 'other_non_current_assets', l: 'Other Non-Current Assets' },
      ]},
    ],
  },
  {
    key: 'liabilities',
    label: 'Liabilities',
    accent: 'rose',
    subsections: [
      { key: 'current', label: 'Current Liabilities', fields: [
        { k: 'accounts_payable', l: 'Accounts Payable' },
        { k: 'accrued_expenses', l: 'Accrued Expenses' },
        { k: 'short_term_debt', l: 'Short-Term Debt / Notes Due < 1 yr' },
        { k: 'credit_cards', l: 'Credit Cards' },
        { k: 'taxes_payable', l: 'Taxes Payable' },
        { k: 'other_current_liabilities', l: 'Other Current Liabilities' },
      ]},
      { key: 'long_term', label: 'Long-Term Liabilities', fields: [
        { k: 'mortgages_payable', l: 'Mortgages Payable' },
        { k: 'notes_payable', l: 'Notes Payable (>1 yr)' },
        { k: 'equipment_loans', l: 'Equipment Loans' },
        { k: 'sba_loans', l: 'SBA Loans' },
        { k: 'other_long_term', l: 'Other Long-Term Liabilities' },
      ]},
    ],
  },
  {
    key: 'equity',
    label: "Owner's Equity",
    accent: 'indigo',
    subsections: [
      { key: null, label: null, fields: [
        { k: 'owners_capital', l: "Owner's Capital" },
        { k: 'additional_paid_in_capital', l: 'Additional Paid-In Capital' },
        { k: 'retained_earnings', l: 'Retained Earnings' },
        { k: 'treasury_stock', l: 'Treasury Stock' },
        { k: 'other_equity', l: 'Other Equity' },
      ]},
    ],
  },
];

const fmt = (v) => {
  const n = Number(v) || 0;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const sumLeaves = (obj) => {
  if (!obj) return 0;
  let total = 0;
  for (const v of Object.values(obj)) {
    if (typeof v === 'object' && v !== null) total += sumLeaves(v);
    else if (v !== '' && v !== null && v !== undefined) total += Number(v) || 0;
  }
  return total;
};

const BalanceSheetCard = ({
  applicationId,
  linkToken,
  sharedUnderwritingToken,
  data: initialData = {},
  onSave,
  authToken,
  businessName,
  readOnly = false,
  uploadedFiles = [],
}) => {
  const [mode, setMode] = useState('view');
  const [data, setData] = useState(initialData || {});
  const [saving, setSaving] = useState(false);
  const [generatingShare, setGeneratingShare] = useState(false);
  const [generatedShareLink, setGeneratedShareLink] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  // Collapse the body by default if there's no data — auto-expand when there is.
  const [collapsed, setCollapsed] = useState(false);
  const uploadRef = React.useRef(null);

  useEffect(() => { setData(initialData || {}); }, [initialData]);

  const isShared = !!sharedUnderwritingToken;
  const isPublicForm = !!linkToken;
  const isAuth = !!applicationId && !isPublicForm && !isShared;
  const canEdit = !readOnly && !isShared;

  const headers = () => {
    const h = {};
    if (authToken) h['Authorization'] = `Bearer ${authToken}`;
    return h;
  };

  const setLeaf = (sectionKey, subKey, fieldKey, value) => {
    setData(prev => {
      const next = { ...prev };
      const section = { ...(next[sectionKey] || {}) };
      if (subKey) {
        const sub = { ...(section[subKey] || {}) };
        sub[fieldKey] = value === '' ? null : Number(value);
        section[subKey] = sub;
      } else {
        section[fieldKey] = value === '' ? null : Number(value);
      }
      next[sectionKey] = section;
      return next;
    });
  };

  const setMeta = (key, value) => {
    setData(prev => ({ ...prev, [key]: value }));
  };

  const totals = useMemo(() => {
    const t = {};
    for (const g of BALANCE_SHEET_GROUPS) {
      t[g.key] = sumLeaves(data?.[g.key]);
    }
    t.liabPlusEquity = t.liabilities + t.equity;
    t.diff = t.assets - t.liabPlusEquity;
    return t;
  }, [data]);

  const hasData = totals.assets > 0 || totals.liabilities > 0 || totals.equity > 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = isPublicForm
        ? `${BACKEND_URL}/api/balance-sheet-link/${linkToken}/save-draft`
        : `${BACKEND_URL}/api/applications/${applicationId}/balance-sheet`;
      const resp = await axios.put(url, { data }, { headers: headers() });
      const saved = resp.data?.data || data;
      setData(saved);
      setMode('view');
      if (onSave) onSave(saved);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save Balance Sheet');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const reqHeaders = {};
      let url;
      if (isShared) {
        url = `${BACKEND_URL}/api/shared/underwriting/${sharedUnderwritingToken}/balance-sheet/pdf`;
      } else if (isPublicForm) {
        url = `${BACKEND_URL}/api/balance-sheet-link/${linkToken}/pdf`;
      } else {
        url = `${BACKEND_URL}/api/applications/balance-sheet-pdf/${applicationId}`;
        if (authToken) reqHeaders['Authorization'] = `Bearer ${authToken}`;
      }
      const resp = await axios.get(url, { headers: reqHeaders, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `balance_sheet_${applicationId || linkToken || 'export'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 250);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to download PDF');
    }
  };

  const handleGenerateShareLink = async () => {
    if (!isAuth) return;
    setGeneratingShare(true);
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/applications/${applicationId}/balance-sheet/generate-link`,
        {},
        { headers: headers() }
      );
      setGeneratedShareLink(`${window.location.origin}/balance-sheet/${resp.data.token}`);
    } catch (err) {
      alert('Failed to generate share link');
    } finally {
      setGeneratingShare(false);
    }
  };

  const handleUploadBalanceSheet = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      let url;
      const h = {};
      if (isPublicForm) {
        url = `${BACKEND_URL}/api/balance-sheet-link/${linkToken}/upload`;
      } else {
        url = `${BACKEND_URL}/api/applications/${applicationId}/balance-sheet/upload`;
        if (authToken) h['Authorization'] = `Bearer ${authToken}`;
      }
      await axios.post(url, form, { headers: h });
      if (onSave) onSave(data);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (uploadRef.current) uploadRef.current.value = '';
    }
  };

  // Remove an uploaded balance sheet file (auth-only — public link users cannot
  // delete). Backend guards deletion with _assert_can_delete so brokers can
  // clean up their own incomplete apps, underwriters/admins can always delete.
  const handleDeleteBalanceSheetFile = async (fileId, filename) => {
    if (!isAuth || !applicationId || !fileId) return;
    if (!window.confirm(`Delete "${filename || 'this file'}"? This cannot be undone.`)) return;
    setDeletingFileId(fileId);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/applications/${applicationId}/balance-sheet/file/${fileId}`,
        { headers: headers() },
      );
      if (onSave) onSave(data);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete file');
    } finally {
      setDeletingFileId(null);
    }
  };

  const renderField = (sectionKey, subKey, field) => {
    const value = subKey
      ? (data?.[sectionKey]?.[subKey]?.[field.k] ?? '')
      : (data?.[sectionKey]?.[field.k] ?? '');
    if (mode === 'edit') {
      return (
        <div className="grid grid-cols-12 gap-2 items-center py-0.5" key={field.k}>
          <Label className="col-span-7 text-xs text-slate-700 truncate">{field.l}</Label>
          <Input
            type="number"
            step="0.01"
            value={value === null || value === undefined ? '' : value}
            onChange={e => setLeaf(sectionKey, subKey, field.k, e.target.value)}
            placeholder="0.00"
            className="col-span-5 h-8 text-sm text-right"
            data-testid={`bs-input-${sectionKey}-${subKey || 'flat'}-${field.k}`}
          />
        </div>
      );
    }
    const numeric = Number(value) || 0;
    return (
      <div className="flex justify-between py-0.5 text-sm" key={field.k}>
        <span className="text-slate-700">{field.l}</span>
        <span className={numeric ? 'text-slate-900 font-medium' : 'text-slate-400'}>{fmt(numeric)}</span>
      </div>
    );
  };

  const accentBg = {
    emerald: 'bg-emerald-50 border-emerald-200',
    rose:    'bg-rose-50 border-rose-200',
    indigo:  'bg-indigo-50 border-indigo-200',
  };
  const accentText = {
    emerald: 'text-emerald-800',
    rose:    'text-rose-800',
    indigo:  'text-indigo-800',
  };

  return (
    <div className={`border rounded-xl p-4 ${hasData ? 'bg-cyan-50/30 border-cyan-200' : 'bg-slate-50 border-slate-200'}`} data-testid="balance-sheet-card">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <button
          type="button"
          onClick={() => setCollapsed(v => !v)}
          className="flex items-center gap-2 min-w-0 group flex-1 text-left"
          data-testid="bs-toggle-collapse"
          title={collapsed ? 'Expand balance sheet' : 'Collapse balance sheet'}
        >
          {collapsed
            ? <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-slate-700 shrink-0" />
            : <ChevronDown className="h-4 w-4 text-slate-500 group-hover:text-slate-700 shrink-0" />}
          <ClipboardList className={`h-4 w-4 ${hasData ? 'text-cyan-700' : 'text-slate-500'} shrink-0`} />
          <h4 className={`text-sm font-bold ${hasData ? 'text-cyan-900' : 'text-slate-700'}`}>Balance Sheet</h4>
          {businessName && <span className="text-xs text-slate-500 truncate">— {businessName}</span>}
          {hasData && (
            <span className="text-[11px] bg-cyan-100 text-cyan-800 px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">
              Assets {fmt(totals.assets)}
            </span>
          )}
        </button>
        <div className="flex items-center gap-1.5">
          {hasData && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleDownloadPdf} data-testid="bs-download-pdf-btn">
              <Download className="h-3 w-3 mr-1" /> Download PDF
            </Button>
          )}
          {!isShared && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-slate-300 text-slate-600 hover:bg-slate-100"
                onClick={() => uploadRef.current?.click()}
                disabled={uploading}
                data-testid="bs-upload-btn"
              >
                {uploading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />} Upload
              </Button>
              <input
                ref={uploadRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.csv"
                onChange={handleUploadBalanceSheet}
              />
            </>
          )}
          {isAuth && (
            <Button size="sm" variant="outline" className="h-7 text-xs border-teal-400 text-teal-700 hover:bg-teal-50" onClick={handleGenerateShareLink} disabled={generatingShare} data-testid="bs-share-btn">
              {generatingShare ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Link2 className="h-3 w-3 mr-1" />} Share Input Form
            </Button>
          )}
          {canEdit && mode === 'view' && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setMode('edit')} data-testid="bs-edit-btn">
              <Pencil className="h-3 w-3 mr-1" /> {hasData ? 'Edit' : 'Add Data'}
            </Button>
          )}
        </div>
      </div>

      {generatedShareLink && (
        <div className="mb-3 p-2 bg-teal-50 border border-teal-200 rounded text-xs text-teal-900 flex items-center gap-2" data-testid="bs-share-link-display">
          <Link2 className="h-3 w-3 shrink-0" />
          <input readOnly value={generatedShareLink} className="flex-1 bg-transparent outline-none truncate" onFocus={(e) => e.target.select()} />
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => { navigator.clipboard.writeText(generatedShareLink); }}>Copy</Button>
        </div>
      )}

      {/* Uploaded balance sheet files (uploaded PDFs/docs alongside the form) */}
      {(uploadedFiles || []).length > 0 && (
        <div className="mb-3 space-y-1" data-testid="bs-uploaded-files">
          <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Uploaded Balance Sheet Files</p>
          {uploadedFiles.map((f, fi) => (
            <div key={f.file_id || fi} className="flex items-center gap-2 text-xs text-slate-700 bg-white border border-cyan-100 rounded-lg px-3 py-1.5">
              <FileTextIcon className="h-3 w-3 text-cyan-600 shrink-0" />
              <span className="truncate flex-1">{f.filename}</span>
              {applicationId && f.stored_filename && (
                <a
                  href={`${BACKEND_URL}/api/files/${applicationId}/${f.stored_filename}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-cyan-700 hover:text-cyan-900 shrink-0"
                  title="Download file"
                  data-testid={`bs-file-download-${fi}`}
                >
                  <Download className="h-3 w-3" />
                </a>
              )}
              {isAuth && f.file_id && (
                <button
                  type="button"
                  onClick={() => handleDeleteBalanceSheetFile(f.file_id, f.filename)}
                  disabled={deletingFileId === f.file_id}
                  className="text-rose-500 hover:text-rose-700 disabled:opacity-50 shrink-0"
                  title="Delete file"
                  data-testid={`bs-file-delete-${fi}`}
                >
                  {deletingFileId === f.file_id
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : <Trash2 className="h-3 w-3" />}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!collapsed && (
        <>
          {/* As-of date + currency */}
          {(mode === 'edit' || data?.as_of_date) && (
            <div className="grid grid-cols-12 gap-2 mb-3">
              <div className="col-span-7 md:col-span-4">
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">As of Date</Label>
                {mode === 'edit' ? (
                  <Input type="date" value={data?.as_of_date || ''} onChange={e => setMeta('as_of_date', e.target.value)} className="h-8 text-sm" data-testid="bs-input-as-of-date" />
                ) : (
                  <div className="text-sm font-medium text-slate-900">{data?.as_of_date || '—'}</div>
                )}
              </div>
            </div>
          )}

      {/* 3 sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {BALANCE_SHEET_GROUPS.map(g => (
          <div key={g.key} className={`rounded-lg border p-3 ${accentBg[g.accent]}`} data-testid={`bs-section-${g.key}`}>
            <h5 className={`text-xs font-bold uppercase tracking-wide ${accentText[g.accent]} mb-2`}>{g.label}</h5>
            <div className="space-y-2">
              {g.subsections.map(sub => (
                <div key={sub.key || 'flat'}>
                  {sub.label && (
                    <p className={`text-[10px] uppercase tracking-wide font-semibold ${accentText[g.accent]} opacity-70 mb-0.5`}>{sub.label}</p>
                  )}
                  {sub.fields.map(f => renderField(g.key, sub.key, f))}
                  {sub.label && (
                    <div className={`flex justify-between text-xs font-bold pt-1 border-t border-slate-300 ${accentText[g.accent]}`}>
                      <span>Total {sub.label}</span>
                      <span>{fmt(sumLeaves(data?.[g.key]?.[sub.key]))}</span>
                    </div>
                  )}
                </div>
              ))}
              <div className={`flex justify-between text-sm font-bold pt-2 border-t-2 border-slate-700 ${accentText[g.accent]}`} data-testid={`bs-total-${g.key}`}>
                <span>TOTAL {g.label.toUpperCase()}</span>
                <span>{fmt(totals[g.key])}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Notes */}
      {(mode === 'edit' || data?.notes) && (
        <div className="mt-3">
          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Notes</Label>
          {mode === 'edit' ? (
            <textarea
              rows={2}
              value={data?.notes || ''}
              onChange={e => setMeta('notes', e.target.value)}
              className="w-full text-sm rounded-md border border-input bg-background px-2 py-1.5"
              placeholder="Anything to note about this snapshot..."
              data-testid="bs-input-notes"
            />
          ) : (
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{data?.notes}</p>
          )}
        </div>
      )}

      {/* Balance check */}
      {hasData && (
        <div className={`mt-3 p-3 rounded-md text-sm font-semibold ${Math.abs(totals.diff) < 1 ? 'bg-emerald-100 text-emerald-900 border border-emerald-200' : 'bg-amber-50 text-amber-900 border border-amber-200'}`} data-testid="bs-balance-check">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {Math.abs(totals.diff) < 1 ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
              <span>
                {Math.abs(totals.diff) < 1
                  ? 'Balance Sheet is in balance.'
                  : `Out of balance by ${fmt(Math.abs(totals.diff))}`}
              </span>
            </div>
            <div className="text-xs font-normal text-slate-700">
              Assets {fmt(totals.assets)} = Liab {fmt(totals.liabilities)} + Equity {fmt(totals.equity)}
            </div>
          </div>
        </div>
      )}

      {mode === 'edit' && (
        <div className="flex justify-end gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={() => { setData(initialData || {}); setMode('view'); }} disabled={saving}>Cancel</Button>
          <Button size="sm" className="bg-cyan-700 hover:bg-cyan-800 text-white" onClick={handleSave} disabled={saving} data-testid="bs-save-btn">
            {saving ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Saving...</> : <><Save className="h-3 w-3 mr-1" /> Save</>}
          </Button>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default BalanceSheetCard;
